import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import './i18n';

// ========== LANDING PAGES ==========
import LandingPage from './pages/LandingPage';
import RegisterCompanyPage from './pages/RegisterCompanyPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import DocumentationPage from './pages/DocumentationPage';

// ========== DOCUMENTATION PAGES ==========
import QuickStartGuidePage from './pages/docs/QuickStartGuidePage';
import GoogleCalendarDocPage from './pages/docs/GoogleCalendarDocPage';
import GenericDocPage from './pages/docs/GenericDocPage';

// ========== MOBILE APP ==========
import MobileLoginPage from './pages/MobileLoginPage';
import SetupWizardPage from './pages/SetupWizardPage';
import CustomerDashboard from './pages/mobile/CustomerDashboard';
import CustomerSetupWizard from './pages/mobile/CustomerSetupWizard';
import CustomerChatPage from './pages/mobile/CustomerChatPage';
import CustomerInventoryPage from './pages/mobile/CustomerInventoryPage';
import CustomerJobDetailPage from './pages/mobile/CustomerJobDetailPage';
import CustomerEstimatePage from './pages/mobile/CustomerEstimatePage';
import PorterDashboard from './pages/mobile/PorterDashboard';
import SalesRepDashboard from './pages/mobile/SalesRepDashboard';
import DriverDashboard from './pages/mobile/DriverDashboard';

// ========== CONTROL PANEL ==========
import ControlPanelLayout from './pages/control-panel/ControlPanelLayout';
import CompanyDashboard from './pages/control-panel/CompanyDashboard';
import UserManagement from './pages/control-panel/UserManagement';
import JobManagement from './pages/control-panel/JobManagement';
import CreateJobPage from './pages/control-panel/CreateJobPage';
import CompanySettings from './pages/control-panel/CompanySettings';
import QRCodeManagement from './pages/control-panel/QRCodeManagement';
import AICreditsManagement from './pages/control-panel/AICreditsManagement';
import BillingPage from './pages/control-panel/BillingPage';
import WhiteLabelSettings from './pages/control-panel/WhiteLabelSettings';
import NotificationSettings from './pages/control-panel/NotificationSettings';
import MessagesPage from './pages/control-panel/MessagesPage';
import EmployeeChatPage from './pages/control-panel/EmployeeChatPage';
import NotificationTemplates from './pages/control-panel/NotificationTemplates';
import SupportTickets from './pages/control-panel/SupportTickets';
import StorageManagement from './pages/control-panel/StorageManagement';
import EstimateSettings from './pages/control-panel/EstimateSettings';
import TimeTracking from './pages/control-panel/TimeTracking';
import ScanConsole from './pages/control-panel/ScanConsole';

// ========== SUPERADMIN ==========
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SuperAdminLogin from './pages/superadmin/SuperAdminLogin';
import SuperadminStorageManagement from './pages/superadmin/StorageManagement';
import SuperAdminLayout from './pages/superadmin/SuperAdminLayout';
import SuperAdminOverview from './pages/superadmin/SuperAdminOverview';
import MaintenanceManagement from './pages/superadmin/MaintenanceManagement';

// Shared
import JobDetails from './pages/JobDetails';
import CreateJob from './pages/CreateJob';
import InventoryManager from './pages/InventoryManager';
import AIScanner from './pages/AIScanner';

// Maintenance
import MaintenanceBanner from './components/MaintenanceBanner';
import MaintenancePage from './pages/MaintenancePage';

// Protected Route Component
const ProtectedRoute = ({ children, requiresControlPanel = false, requiresSuperAdmin = false }) => {
  const token = localStorage.getItem('movemate_token');
  const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
  
  if (!token) {
    if (requiresSuperAdmin) {
      return <Navigate to="/superadmin/login" replace />;
    }
    return <Navigate to={requiresControlPanel ? "/login" : "/mobile-login"} replace />;
  }
  
  // Superadmin only for super_admin role
  if (requiresSuperAdmin && user.role !== 'super_admin') {
    return <Navigate to="/superadmin/login" replace />;
  }
  
  // Control panel only for role level >= 20 AND not mobile-only roles
  const mobileOnlyRoles = ['driver_crew', 'porter', 'customer', 'customer_commercial'];
  if (requiresControlPanel) {
    if (user.role_level < 20 || mobileOnlyRoles.includes(user.role)) {
      return <Navigate to="/mobile/dashboard" replace />;
    }
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('movemate_token');
    const userData = localStorage.getItem('movemate_user');
    
    if (token && userData && userData !== 'undefined') {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        // Invalid JSON in localStorage - clear it
        localStorage.removeItem('movemate_token');
        localStorage.removeItem('movemate_user');
      }
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          {/* Maintenance Warning Banner - shows for logged-in users */}
          {user && <MaintenanceBanner />}
          <Routes>
            {/* ========== MAINTENANCE PAGE ========== */}
            <Route path="/maintenance" element={<MaintenancePage />} />
            
            {/* ========== LANDING PAGES ========== */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/register-company" element={<RegisterCompanyPage setUser={setUser} />} />
            <Route path="/login" element={<LoginPage setUser={setUser} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsConditionsPage />} />
            <Route path="/docs" element={<DocumentationPage />} />
            <Route path="/docs/quick-start" element={<QuickStartGuidePage />} />
            <Route path="/docs/google-calendar" element={<GoogleCalendarDocPage />} />
            <Route path="/docs/:slug" element={<GenericDocPage />} />
          
          {/* ========== MOBILE APP ========== */}
          {/* Mobile root - redirect to login or dashboard */}
          <Route 
            path="/mobile" 
            element={
              user ? <Navigate to="/mobile/dashboard" replace /> : <Navigate to="/mobile-login" replace />
            } 
          />
          <Route path="/mobile-login" element={<MobileLoginPage setUser={setUser} />} />
          <Route
            path="/setup-wizard"
            element={
              <ProtectedRoute>
                <SetupWizardPage />
              </ProtectedRoute>
            }
          />
          
          {/* Customer Setup Wizard (Mobile) */}
          <Route
            path="/mobile/setup"
            element={
              <ProtectedRoute>
                <CustomerSetupWizard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          {/* Mobile Dashboard - Role-based routing */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <MobileDashboardRouter user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mobile/dashboard"
            element={
              <ProtectedRoute>
                <MobileDashboardRouter user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mobile/chat"
            element={
              <ProtectedRoute>
                <CustomerChatPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mobile/inventory"
            element={
              <ProtectedRoute>
                <CustomerInventoryPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mobile/job/:jobId"
            element={
              <ProtectedRoute>
                <CustomerJobDetailPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mobile/estimate"
            element={
              <ProtectedRoute>
                <CustomerEstimatePage user={user} />
              </ProtectedRoute>
            }
          />
          
          {/* ========== CONTROL PANEL ========== */}
          <Route
            path="/control-panel"
            element={
              <ProtectedRoute requiresControlPanel>
                <ControlPanelLayout user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          >
            <Route index element={<CompanyDashboard />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="jobs" element={<JobManagement />} />
            <Route path="jobs/create" element={<CreateJobPage />} />
            <Route path="jobs/:jobId" element={<JobDetails user={user} />} />
            <Route path="inventory" element={<InventoryManager />} />
            <Route path="scan-console" element={<ScanConsole />} />
            <Route path="qr-codes" element={<QRCodeManagement />} />
            <Route path="ai-credits" element={<AICreditsManagement />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="team-chat" element={<EmployeeChatPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="branding" element={<WhiteLabelSettings />} />
            <Route path="notifications" element={<NotificationSettings />} />
            <Route path="notification-templates" element={<NotificationTemplates />} />
            <Route path="support" element={<SupportTickets />} />
            <Route path="storage" element={<StorageManagement />} />
            <Route path="estimate-settings" element={<EstimateSettings />} />
            <Route path="time-tracking" element={<TimeTracking />} />
            <Route path="settings" element={<CompanySettings />} />
          </Route>
          
          {/* ========== SUPERADMIN PANEL ========== */}
          <Route
            path="/superadmin/login"
            element={<SuperAdminLogin setUser={setUser} />}
          />
          <Route
            path="/superadmin"
            element={
              <ProtectedRoute requiresSuperAdmin>
                <SuperAdminLayout user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          >
            <Route index element={<SuperAdminOverview />} />
            <Route path="dashboard" element={<SuperAdminDashboard user={user} setUser={setUser} />} />
            <Route path="storage" element={<SuperadminStorageManagement />} />
            <Route path="maintenance" element={<MaintenanceManagement />} />
            <Route path="companies" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="companies" />} />
            <Route path="subscriptions" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="subscriptions" />} />
            <Route path="admins" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="admins" />} />
            <Route path="approvals" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="approvals" />} />
            <Route path="tickets" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="tickets" />} />
            <Route path="messaging" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="messaging" />} />
            <Route path="activity" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="activity" />} />
            <Route path="audit" element={<SuperAdminDashboard user={user} setUser={setUser} defaultTab="audit" />} />
          </Route>
          
          {/* ========== SHARED ROUTES ========== */}
          {/* Redirect old job create route to new location */}
          <Route
            path="/jobs/create"
            element={<Navigate to="/control-panel/jobs/create" replace />}
          />
          
          <Route
            path="/jobs/:jobId"
            element={
              <ProtectedRoute>
                <JobDetails user={user} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <InventoryManager />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/ai-scanner"
            element={
              <ProtectedRoute>
                <AIScanner />
              </ProtectedRoute>
            }
          />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster 
          position="top-right" 
          richColors 
          duration={4000}
          toastOptions={{
            error: {
              duration: 8000,
              style: {
                background: '#FEE2E2',
                border: '1px solid #EF4444',
                color: '#DC2626',
              },
            },
            success: {
              duration: 4000,
            },
            warning: {
              duration: 6000,
            },
          }}
        />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

// Mobile Dashboard Router - Routes based on role
const MobileDashboardRouter = ({ user, setUser }) => {
  if (!user) return <Navigate to="/mobile-login" replace />;
  
  // Route based on role
  if (user.role === 'driver_crew') {
    return <DriverDashboard user={user} setUser={setUser} />;
  } else if (user.role === 'porter') {
    return <PorterDashboard user={user} setUser={setUser} />;
  } else if (user.role === 'sales_rep') {
    return <SalesRepDashboard user={user} setUser={setUser} />;
  } else if (user.role_level >= 20) {
    // Redirect company staff to control panel
    return <Navigate to="/control-panel" replace />;
  } else {
    // Default to customer dashboard
    return <CustomerDashboard user={user} setUser={setUser} />;
  }
};

export default App;
